import * as Types from '../types/types';
import { formatApiRequest, toCamelCase } from 'types/conversion';

export const TOKEN_STORAGE = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  USER_DATA: 'userData',
  TOKEN_EXPIRY: 'tokenExpiry',
  USER_TYPE: 'userType',
  IS_GUEST: 'isGuest',
  PENDING_VERIFICATION_EMAIL: 'pendingVerificationEmail',
} as const;

export function clearStoredAuth(): void {
  localStorage.removeItem(TOKEN_STORAGE.ACCESS_TOKEN);
  localStorage.removeItem(TOKEN_STORAGE.REFRESH_TOKEN);
  localStorage.removeItem(TOKEN_STORAGE.USER_DATA);
  localStorage.removeItem(TOKEN_STORAGE.TOKEN_EXPIRY);
  localStorage.removeItem(TOKEN_STORAGE.USER_TYPE);
  localStorage.removeItem(TOKEN_STORAGE.IS_GUEST);
}

export function prepareUserDataForStorage(user: Types.User): string {
  try {
    return JSON.stringify(formatApiRequest(user));
  } catch (error) {
    console.error('Failed to prepare user data for storage:', error);
    return JSON.stringify(user);
  }
}

export function parseStoredUserData(userDataStr: string): Types.User | null {
  try {
    return toCamelCase<Types.User>(JSON.parse(userDataStr));
  } catch (error) {
    console.error('Failed to parse stored user data:', error);
    return null;
  }
}

export function updateStoredUserData(user: Types.User): void {
  try {
    localStorage.setItem(TOKEN_STORAGE.USER_DATA, prepareUserDataForStorage(user));
  } catch (error) {
    console.error('Failed to update stored user data:', error);
  }
}

export function storeAuthData(authResponse: Types.AuthResponse, isGuest = false): void {
  localStorage.setItem(TOKEN_STORAGE.ACCESS_TOKEN, authResponse.accessToken);
  localStorage.setItem(TOKEN_STORAGE.REFRESH_TOKEN, authResponse.refreshToken);
  localStorage.setItem(TOKEN_STORAGE.USER_DATA, prepareUserDataForStorage(authResponse.user));
  localStorage.setItem(TOKEN_STORAGE.TOKEN_EXPIRY, authResponse.expiresAt.toString());
  localStorage.setItem(TOKEN_STORAGE.USER_TYPE, authResponse.user.userType);
  localStorage.setItem(TOKEN_STORAGE.IS_GUEST, isGuest.toString());
}

export function getStoredAuthData(): {
  accessToken: string | null;
  refreshToken: string | null;
  userData: Types.User | null;
  expiresAt: number | null;
  userType: string | null;
  isGuest: boolean;
} {
  const accessToken = localStorage.getItem(TOKEN_STORAGE.ACCESS_TOKEN);
  const refreshToken = localStorage.getItem(TOKEN_STORAGE.REFRESH_TOKEN);
  const userDataStr = localStorage.getItem(TOKEN_STORAGE.USER_DATA);
  const expiryStr = localStorage.getItem(TOKEN_STORAGE.TOKEN_EXPIRY);
  const userType = localStorage.getItem(TOKEN_STORAGE.USER_TYPE);
  const isGuestStr = localStorage.getItem(TOKEN_STORAGE.IS_GUEST);

  let userData: Types.User | null = null;
  let expiresAt: number | null = null;
  try {
    if (userDataStr) userData = parseStoredUserData(userDataStr);
    if (expiryStr) expiresAt = parseInt(expiryStr, 10);
  } catch (error) {
    console.error('Failed to parse stored auth data:', error);
    clearStoredAuth();
  }

  return {
    accessToken,
    refreshToken,
    userData,
    expiresAt,
    userType,
    isGuest: isGuestStr === 'true',
  };
}
